import { err, ok, type Result } from '../shared/result.js';

export const REGISTRATION_SCHEMES = [
  'MA_RC',
  'MA_ICE',
  'MA_IF',
  'FR_SIREN',
  'FR_SIRET',
  'EU_VAT',
  'US_EIN',
  'GB_CRN',
  'GB_VAT',
  'OTHER',
] as const;

export type RegistrationScheme = (typeof REGISTRATION_SCHEMES)[number];

export type LegalRegistration = {
  scheme: RegistrationScheme;
  value: string;
  /** Required when scheme is OTHER. */
  label?: string;
};

export type RegistrationSchemeOption = {
  scheme: RegistrationScheme;
  label: string;
  hint: string | null;
};

const SCHEME_SET = new Set<string>(REGISTRATION_SCHEMES);

const META: Record<
  RegistrationScheme,
  { label: string; hint: string | null; pattern: RegExp | null }
> = {
  MA_RC: { label: 'RC', hint: null, pattern: null },
  MA_ICE: { label: 'ICE', hint: '15 chiffres', pattern: /^\d{15}$/ },
  MA_IF: { label: 'IF', hint: null, pattern: null },
  FR_SIREN: { label: 'SIREN', hint: '9 chiffres', pattern: /^\d{9}$/ },
  FR_SIRET: { label: 'SIRET', hint: '14 chiffres', pattern: /^\d{14}$/ },
  EU_VAT: { label: 'TVA', hint: 'préfixe pays + numéro', pattern: /^[A-Z]{2}[A-Z0-9]{2,12}$/i },
  US_EIN: { label: 'EIN', hint: 'NN-NNNNNNN', pattern: /^\d{2}-?\d{7}$/ },
  GB_CRN: { label: 'CRN', hint: null, pattern: null },
  GB_VAT: { label: 'VAT', hint: null, pattern: null },
  OTHER: { label: 'Autre', hint: null, pattern: null },
};

const EU = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT',
  'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
]);

export function isRegistrationScheme(value: string): value is RegistrationScheme {
  return SCHEME_SET.has(value);
}

export function schemeLabel(scheme: RegistrationScheme): string {
  return META[scheme].label;
}

/** Schemes the form shows for a country. OTHER is extra, not in this list. */
export function schemesForCountry(countryCode: string | null | undefined): RegistrationSchemeOption[] {
  const cc = countryCode?.trim().toUpperCase() ?? '';
  let schemes: RegistrationScheme[] = [];
  if (cc === 'MA') schemes = ['MA_RC', 'MA_ICE', 'MA_IF'];
  else if (cc === 'FR') schemes = ['FR_SIREN', 'FR_SIRET', 'EU_VAT'];
  else if (cc === 'US') schemes = ['US_EIN'];
  else if (cc === 'GB') schemes = ['GB_CRN', 'GB_VAT'];
  else if (EU.has(cc)) schemes = ['EU_VAT'];
  return schemes.map((scheme) => ({
    scheme,
    label: META[scheme].label,
    hint: META[scheme].hint,
  }));
}

/** Advisory mismatch text. Null when the value matches or the scheme has no pattern. */
export function formatHint(scheme: RegistrationScheme, value: string): string | null {
  const meta = META[scheme];
  const v = value.trim();
  if (!v || !meta.pattern || !meta.hint) return null;
  return meta.pattern.test(v) ? null : meta.hint;
}

function dedupeKey(row: LegalRegistration): string {
  if (row.scheme === 'OTHER') return `OTHER:${row.label ?? ''}`;
  return row.scheme;
}

export function normalizeRegistrations(input: unknown): Result<LegalRegistration[]> {
  if (input == null) return ok([]);
  if (!Array.isArray(input)) return err('registrations must be a list');
  const out: LegalRegistration[] = [];
  const seen = new Set<string>();
  for (const raw of input) {
    if (!raw || typeof raw !== 'object') return err('registration row is invalid');
    const row = raw as { scheme?: unknown; value?: unknown; label?: unknown };
    const scheme = typeof row.scheme === 'string' ? row.scheme.trim() : '';
    if (!isRegistrationScheme(scheme)) return err(`unknown registration scheme: ${scheme || 'empty'}`);
    const value = typeof row.value === 'string' ? row.value.trim() : '';
    if (!value) continue;
    const label = typeof row.label === 'string' ? row.label.trim() : '';
    if (scheme === 'OTHER' && !label) return err('OTHER registration requires a label');
    const next: LegalRegistration =
      scheme === 'OTHER' ? { scheme, value, label } : { scheme, value };
    const key = dedupeKey(next);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(next);
  }
  return ok(out);
}

export function registrationValue(
  list: LegalRegistration[] | null | undefined,
  scheme: RegistrationScheme,
): string | null {
  const row = list?.find((r) => r.scheme === scheme && r.value.trim());
  return row?.value.trim() || null;
}

/** Country schemes first, then the rest, joined for a convention sentence. */
export function formatRegistrationsLine(
  list: LegalRegistration[] | null | undefined,
  countryCode?: string | null,
): string | null {
  const rows = (list ?? []).filter((r) => r.value.trim());
  if (rows.length === 0) return null;
  const preferred = schemesForCountry(countryCode).map((s) => s.scheme);
  const preferredSet = new Set(preferred);
  const ordered = [
    ...preferred.flatMap((scheme) => rows.filter((r) => r.scheme === scheme)),
    ...rows.filter((r) => !preferredSet.has(r.scheme)),
  ];
  const parts = ordered.map((r) => {
    const name = r.scheme === 'OTHER' ? (r.label?.trim() || 'Autre') : META[r.scheme].label;
    return `${name} ${r.value.trim()}`;
  });
  return parts.join(' · ');
}

export function legacyToRegistrations(input: {
  rcNumber?: string | null;
  iceNumber?: string | null;
  taxId?: string | null;
}): LegalRegistration[] {
  const rows: LegalRegistration[] = [];
  const rc = input.rcNumber?.trim();
  const ice = input.iceNumber?.trim();
  const tax = input.taxId?.trim();
  if (rc) rows.push({ scheme: 'MA_RC', value: rc });
  if (ice) rows.push({ scheme: 'MA_ICE', value: ice });
  if (tax) rows.push({ scheme: 'MA_IF', value: tax });
  return rows;
}

/** French country name for an ISO code. Non-codes are returned unchanged. */
export function countryDisplayName(raw: string | null | undefined): string | null {
  const t = raw?.trim();
  if (!t) return null;
  if (!/^[A-Za-z]{2}$/.test(t)) return t;
  try {
    const name = new Intl.DisplayNames(['fr'], { type: 'region' }).of(t.toUpperCase());
    if (name && name.toUpperCase() !== t.toUpperCase()) return name;
  } catch {
    return t.toUpperCase();
  }
  return t.toUpperCase();
}
